import { type QueryObserverResult, useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useMemo, useState } from 'react'

import { getEmployersEmployersId, getGetEmployersEmployersIdQueryKey } from 'utils/web_api'
import { type EmployersEmployer as Employer } from 'utils/web_api.schemas'

interface EmployerContextType {
  employer: Employer | null;
  error: Error | null;
  loading: boolean;
  refetch: ReturnType<typeof useQuery>['refetch'];
  setEmployerId: (id: string | null) => void;
}

export const EmployerContext = createContext<EmployerContextType>({
  employer: null,
  error: null,
  loading: false,
  refetch: () => Promise.resolve({ data: null, error: null, isLoading: false } as QueryObserverResult<unknown, unknown>),
  setEmployerId: () => {},
})

export function useEmployer () {
  return useContext(EmployerContext)
}

interface EmployerProviderProps {
  children: React.ReactNode | ((props: EmployerContextType) => React.ReactNode);
  employerId: string | null;
}

export function EmployerProvider ({
  children,
  employerId,
}: EmployerProviderProps) {
  const [id, setId] = useState<string | null>(employerId)

  const { data: employer, isLoading, error, refetch } = useQuery({
    queryKey: id ? getGetEmployersEmployersIdQueryKey(id) : [],
    queryFn: () => (id ? getEmployersEmployersId(id) : null),
    enabled: !!id,
  })

  const value = useMemo(
    () => ({
      employer: employer || null,
      error,
      loading: isLoading,
      refetch,
      setEmployerId: setId,
    }),
    [employer, isLoading, error, refetch]
  )

  return (
    <EmployerContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </EmployerContext.Provider>
  )
}
